<!-- FormItemAnswer.vue -->
<template>
    <div class="answer" v-if="item.type === 'checkboxes'">
        <div v-for="point in item.answer" :key="point">• {{ point }}</div>
    </div>

    <!-- Doodle -->
    <div class="answer" v-else-if="item.type === 'doodle'">
        <img v-if="item.answer && item.answer !== 'Choose an option'" class="doodle-image" :src="item.answer" />
        <p v-else>Not completed</p>
    </div>

    <!-- Text / Paragraph -->
    <div class="answer" v-else-if="item.type === 'text' || item.type === 'paragraph' || item.type === 'dropdown'">
        <div v-for="(text, idx) in getTextAnswer(item.answer)" :key="`text-${idx}`">
            <div v-for="(line, i) in text.split('\n')" :key="`line-${idx}-${i}`">{{ line }}<br /></div>
        </div>
    </div>

    <!-- Pindrop -->
    <div class="answer" v-else-if="item.type === 'pindrop'">
        <div v-if="getPindropVM(item.answer).lat != ''">LATITUDE: {{ getPindropVM(item.answer).lat }}</div>
        <div v-if="getPindropVM(item.answer).lng != ''">LONGITUDE: {{ getPindropVM(item.answer).lng }}</div>
        <div v-if="getPindropVM(item.answer).w3w">What3Words: {{ getPindropVM(item.answer).w3w }}</div>

        <div class="w3w mt-2 mb-2" v-if="getPindropVM(item.answer).w3w">
            <span class="button is-light is-small" @click="toWhat3Words(getPindropVM(item.answer).w3w)"> <font-awesome-icon icon="link" />&nbsp; View on What3Words </span>
        </div>

        <div v-if="getPindropVM(item.answer).lat && getPindropVM(item.answer).lng" class="map-image" @click="openGoogleMap(getPindropVM(item.answer).lat, getPindropVM(item.answer).lng)">
            <img :src="getMapImage(item.answer, getPindropVM(item.answer).lat, getPindropVM(item.answer).lng)" alt="" />
        </div>
    </div>

    <!-- Datetime -->
    <div class="answer" v-else-if="item.type === 'datetime'">{{ item.answer === 'Choose an option' ? 'Not completed' : simpleDate(item.answer, true) }}</div>

    <!-- Upload -->
    <div class="answer flex" v-else-if="item.type === 'upload'">
        <div v-for="(image, index) in item.answer" :key="`upload-${index}`" class="image-wrap2" @click="zoomIn(image, index)">
            <img :src="image" alt="" />
        </div>
    </div>

    <!-- Crew -->
    <div class="answer" v-else-if="item.type === 'crew'">
        <div class="crew-member" v-for="(crew, ix) in item.answer" :key="`crew-${ix}`">
            <span class="avatar-wrapper hideForPrint">
                <span v-if="crew.avatarUrl !== ''" :style="{ backgroundImage: `url(${crew.avatarUrl})` }" class="avatar"></span>
                <span v-else class="avatar">{{ getAvatar(crew.name) }}</span>
            </span>
            <span class="crew-name">{{ crew.name }}</span>
        </div>
    </div>

    <!-- Risk Matrix -->
    <div class="answer tags" v-else-if="item.type === 'riskmatrix'">
        <div
            class="tag is-medium caps"
            v-for="(text, idx) in getTextAnswer(item.answer)"
            :key="`risk-${idx}`"
            :class="[text === 'high' ? 'is-danger' : text === 'medium' ? 'is-warning' : text === 'low' ? 'is-success' : '']"
        >
            <span>
                {{ text }}
                <br />
            </span>
        </div>
    </div>

    <!-- Dumbtext -->
    <div class="answer pb-3" v-else-if="item.type === 'dumbtext'">
        <div v-for="(text, idx) in getTextAnswer(item.prompt)" :key="`dumbtext-${idx}`">
            <div v-for="(line, i) in text.split('\n')" :key="`dumbline-${idx}-${i}`">{{ line }}<br /></div>
        </div>
    </div>

    <!-- Dumbphoto -->
    <div class="answer flex" v-else-if="item.type === 'dumbphoto'">
        <div class="image-wrap2" @click="zoomIn(item.prompt)">
            <img :src="item.prompt" alt="" />
        </div>
    </div>

    <!-- Number -->
    <div class="answer" v-else-if="item.type === 'number'">
        {{ (item.answer && item.answer[0]) ?? '' }}
    </div>

    <!-- Table -->
    <div class="answer" v-else-if="item.type === 'table'">
        <table class="table">
            <tbody>
                <tr v-for="r in item.tableRows" :key="`r-${r}`">
                    <td v-for="c in item.tableCols" :key="`r-${r}-c-${c}`" class="p-0">
                        <template v-if="(item.tableHeaders === 'both' && (r === 1 || c === 1)) || (item.tableHeaders === 'top' && r === 1) || (item.tableHeaders === 'left' && c === 1)">
                            <div v-if="isCheckbox(item, r, c)" class="sub-content is-flex is-justify-content-center">
                                <span class="material-symbols-outlined">
                                    {{ item.answer[(r - 1) * item.tableCols + (c - 1)] ? 'check_circle' : 'circle' }}
                                </span>
                            </div>
                            <div v-else class="sub-content">
                                {{ item.answer[(r - 1) * item.tableCols + (c - 1)] || '' }}
                            </div>
                        </template>

                        <template v-else>
                            <div v-if="isCheckbox(item, r, c)" class="sub-content is-flex is-justify-content-center">
                                <span class="material-symbols-outlined">
                                    {{ item.answer[(r - 1) * item.tableCols + (c - 1)] ? 'check_circle' : 'circle' }}
                                </span>
                            </div>
                            <div v-else class="sub-content">
                                {{ item.answer[(r - 1) * item.tableCols + (c - 1)] || '' }}
                            </div>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Fallback (optional) -->
    <div class="answer" v-else>
        <!-- Unknown type or not answered -->
    </div>
</template>

<script>
export default {
    name: 'FormItemAnswer',
    props: {
        item: { type: Object, required: true },

        // Pass these in from the parent so you don't duplicate logic
        getTextAnswer: { type: Function, required: true },
        simpleDate: { type: Function, required: true },
        getAvatar: { type: Function, required: true },
        isCheckbox: { type: Function, required: true },
        toWhat3Words: { type: Function, required: true },
        zoomIn: { type: Function, required: true }
    },
    methods: {
        parseCoordinate(input) {
            if (input === null || input === undefined) return null;

            const str = String(input).trim();

            // Detect hemisphere
            const hemisphere = str.match(/[NSEW]/i)?.[0]?.toUpperCase() ?? null;

            // DD (Decimal Degrees)
            const ddMatch = str.match(/^(-?\d+(?:\.\d+)?)/);
            if (ddMatch && !str.includes("'") && !str.includes('"')) {
                let value = parseFloat(ddMatch[1]);
                if (hemisphere === 'S' || hemisphere === 'W') {
                    value = -Math.abs(value);
                }
                return value;
            }

            // DMS (Degrees Minutes Seconds)
            const dmsMatch = str.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
            if (dmsMatch) {
                const degrees = parseFloat(dmsMatch[1]);
                const minutes = parseFloat(dmsMatch[2]);
                const seconds = parseFloat(dmsMatch[3]);

                let value = degrees + minutes / 60 + seconds / 3600;
                if (hemisphere === 'S' || hemisphere === 'W') {
                    value = -value;
                }
                return value;
            }

            // DMM (Degrees Decimal Minutes)
            const dmmMatch = str.match(/(\d+(?:\.\d+)?)\D+(\d+(?:\.\d+)?)/);
            if (dmmMatch) {
                const degrees = parseFloat(dmmMatch[1]);
                const minutes = parseFloat(dmmMatch[2]);

                let value = degrees + minutes / 60;
                if (hemisphere === 'S' || hemisphere === 'W') {
                    value = -value;
                }
                return value;
            }

            return null;
        },

        sanitizeLatLngForStaticMap(latInput, lngInput) {
            const lat = this.parseCoordinate(latInput);
            const lng = this.parseCoordinate(lngInput);

            if (lat === null || lng === null) return null;

            return `${lat.toFixed(6)},${lng.toFixed(6)}`;
        },

        getMapImage(answer, lat, long) {
            let nLat = lat;
            let nLong = long;
            if (!lat || !long) {
                nLat = answer[0] || '';
                nLong = answer[1] || '';
            }
            const center = this.sanitizeLatLngForStaticMap(nLat, nLong);

            const url =
                `https://maps.googleapis.com/maps/api/staticmap` + `?size=600x600` + `&zoom=12` + `&center=${center}` + `&markers=color:red|${center}` + `&key=AIzaSyAWTjw84LiSreWuruKtazxm7jbeK4g0k9U`;

            return url;
        },

        openGoogleMap(lat, long) {
            const nLat = lat;
            const nLong = long;
            const center = this.sanitizeLatLngForStaticMap(nLat, nLong);

            const url = `https://maps.google.com/?q=${center}`;

            window.open(url, '_blank');
        },

        getPindropVM(answer) {
            const lat = answer?.latitude ?? answer?.[0] ?? '';
            const lng = answer?.longitude ?? answer?.[1] ?? '';
            const w3w = answer?.what3words ?? answer?.[2] ?? '';
            const image = answer?.image ?? '';

            return { lat, lng, w3w, image };
        }
    }
};
</script>

<style lang="scss" scoped>
.crew-member {
    margin-bottom: 8px;
}
.map-image {
    width: 200px;
    height: 200px;
    cursor: pointer;
    overflow: hidden;
    border: 1px solid #ddd;
    img {
        width: 100%;
        transition: all 0.3s ease;
        display: block;
    }
    &:hover {
        img {
            transform: scale(1.5);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
    }
}
.image-wrap2 {
    display: inline-block;
    margin-right: 1rem;
    width: 200px;
    border: 1px solid #ddd;
    cursor: pointer;

    img {
        object-fit: contain;
        margin: auto;
        padding: 0;
        height: 250px;
        display: block;
    }
}
.avatar-wrapper {
    width: 45px;
    height: 45px;
    margin-right: 6px;
    span {
        &.avatar {
            border-radius: 50%;
            border: none;
            width: 45px;
            height: 45px;
            overflow: hidden;
            display: inline-block;
            vertical-align: middle;
            text-align: center;
            line-height: 45px;
            background: cadetblue;
            color: white;
            font-weight: bold;
            letter-spacing: 1pt;
            margin-right: 0.6rem;
            background-position: center center;
            background-size: cover;
        }
    }
}
.dotted-wrapper {
    min-height: 70px;
    .placeholder {
        min-height: 70px;
        display: flex;
        align-items: center;
    }
}

.table {
    margin-top: 1rem !important;
    table-layout: fixed !important;
    width: 100% !important;

    tbody {
        tr {
            th {
                border: 1px solid #d4d4d4;
                padding: 12px 20px;
                font-family: 'Open Sans', sans-serif;
                text-align: left !important;
                word-break: break-word;
                position: relative;
                width: 30%;
                font-weight: bold;
            }

            td {
                width: 50% !important;
                border: 1px solid #d4d4d4 !important;
                padding: 12px 20px;
                font-family: 'Open Sans', sans-serif;
                text-align: left !important;
                word-break: break-word;
            }
            &:last-child th {
                border-bottom-width: auto;
            }
        }
    }
}
table {
    width: 100%;
    tbody {
        tr {
            th,
            td {
                .sub-content {
                    word-break: break-word;
                    min-height: 18px;
                }
            }
        }
    }
}
</style>
